import { useState } from 'react';
import { Helmet } from 'react-helmet-async';
import './DrewnikaAccountDeletion.scss';

function DrewnikaAccountDeletion() {
  const [email, setEmail] = useState('');
  const [username, setUsername] = useState('');
  const [reason, setReason] = useState('');
  const [submitted, setSubmitted] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (loading) return;
    setLoading(true);
    setError(null);

    try {
      const response = await fetch('/api/account-deletion', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ app: 'drewnika', email, username, reason }),
      });
      const data = await response.json().catch(() => ({}));

      if (!response.ok || data?.error) {
        setError(data?.error || 'Something went wrong. Please try again.');
        setLoading(false);
        return;
      }
      setSubmitted(true);
    } catch {
      setError('Could not reach the server. Please check your connection and try again.');
    }
    setLoading(false);
  };

  return (
    <>
      <Helmet>
        <title>Account Deletion Request | Drewnika</title>
        <meta name="description" content="Request deletion of your Drewnika account and associated data." />
        <meta name="robots" content="noindex, nofollow" />
      </Helmet>
      <section className="drewnika-deletion">
        <div className="drewnika-deletion-container container">
          <div className="drewnika-deletion-header">
            <h1 className="drewnika-deletion-title">Drewnika Account Deletion</h1>
            <p className="drewnika-deletion-subtitle">
              Request deletion of your account and associated data
            </p>
          </div>

          {submitted ? (
            <div className="drewnika-deletion-success">
              <h2>Request Received</h2>
              <p>
                Your account deletion request has been submitted.
              </p>
              <p>
                We will process it within 30 days and send confirmation to your
                email address.
              </p>
            </div>
          ) : (
            <form className="drewnika-deletion-form" onSubmit={handleSubmit}>
              <div className="drewnika-deletion-info">
                <h2>Delete it yourself, instantly</h2>
                <p>
                  If you can still sign in, you don't need this form. Open
                  Drewnika and go to <strong>Profile &rarr; Delete account</strong>.
                  That removes everything straight away.
                </p>
                <p>
                  Use the form below only if you can no longer access your account.
                </p>
              </div>

              <div className="drewnika-deletion-info">
                <h2>What gets deleted?</h2>
                <ul>
                  <li>Your account and sign-in details</li>
                  <li>Everyone you added as a rater</li>
                  <li>Every movie, meal, dessert, and coffee you logged</li>
                  <li>All ratings and notes attached to them</li>
                  <li>This cannot be undone</li>
                </ul>
                <p className="drewnika-deletion-warning">
                  Your Drewnika sign-in is shared with <strong>Rankful</strong>. Deleting
                  your account removes your access to both apps.
                </p>
              </div>

              <div className="drewnika-deletion-field">
                <label htmlFor="email">Email Address *</label>
                <input
                  type="email"
                  id="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                  placeholder="Enter the email associated with your account"
                />
              </div>

              <div className="drewnika-deletion-field">
                <label htmlFor="username">Display name in Drewnika</label>
                <input
                  type="text"
                  id="username"
                  value={username}
                  onChange={(e) => setUsername(e.target.value)}
                  placeholder="Helps us find your account (optional)"
                />
              </div>

              <div className="drewnika-deletion-field">
                <label htmlFor="reason">Reason for deletion (optional)</label>
                <textarea
                  id="reason"
                  value={reason}
                  onChange={(e) => setReason(e.target.value)}
                  placeholder="Let us know why you're leaving (optional)"
                  rows={3}
                />
              </div>

              {error && <p className="drewnika-deletion-error">{error}</p>}

              <button
                type="submit"
                className="drewnika-deletion-submit"
                disabled={loading}
              >
                {loading ? 'Submitting...' : 'Submit Deletion Request'}
              </button>
            </form>
          )}
        </div>
      </section>
    </>
  );
}

export default DrewnikaAccountDeletion;
